<script lang="ts">
import { defineComponent } from "vue";
import { ApplicationHelper } from "../../../helpers/ApplicationHelper";
import Icon from "../../components/Icon.vue";
import { useSubscriptionStore } from "../../../stores/subscription";

export default defineComponent({
  name: "SubscriptionCard",
  components: { Icon },

  props: {
    subscription: { type: Object, required: true },
    clickable: { type: Boolean, default: false },
    active: { type: Boolean, default: false },
    altRow: { type: Boolean, default: false },
  },

  setup() {
    const subscriptionStore = useSubscriptionStore();
    return {
      subscriptionStore
    };
  },

  data() {
    return {
      nextDate: (this.subscription as any)?.next_payment_date || null as string | null,
    };
  },

  computed: {
    ApplicationHelper() {
      return ApplicationHelper;
    },

    classes(){
      return {
        "subscription-card": true,
        "subscription-card--clickable": this.clickable,
        "subscription-card--alt": this.altRow,
        active: this.active,
      };
    },

    target(){
      const s: any = this.subscription || {};
      return s.donatable_target || s.target || s.donatable || {};
    },

    targetName() {
      return (this.target && (this.target as any).name) || "—";
    },

    //banner_image_url logo in the data card comes from the donatable(campaign/fund and not the organization
    logoUrl(){
      const t: any = this.target || {};
      const s: any = this.subscription || {};
      return t.banner_image_url || t.logo_url || s.organization?.logo_url || null;
    },

    orgInitial() {
      return (this.subscription?.organization_name?.trim()?.charAt(0) || "•").toUpperCase();
    },

    intervalLabel() {
      return this.subscriptionStore.intervalString?.(this.subscription) || "Recurring";
    },

    statusLabel() {
      return this.subscriptionStore.statusString?.(this.subscription) || "Pending";
    },

    displayDate(){
      const s: any = this.subscription || {};
      return s.created_at || s.started_at || s.delay_start_date || null;
    },

    nextGivingLabel(){
      return this.nextDate
        ? this.ApplicationHelper.formatDate(this.nextDate, { time: false, weekday: false })
        : "—";
    },
  },

  async mounted() {
    // Only fetch if we don't already have a next date
    if (this.nextDate) return;

    const s: any = this.subscription || {};
    const id = s.uuid || s.id;
    if (!id) return;

    this.subscriptionStore.setOrganizationId(s.organization_id);
    if (!this.subscriptionStore.subscriptionService) return;

    const res: any = await this.subscriptionStore.subscriptionService.show(id);
    this.nextDate = res?.record?.next_payment_date ?? null;
  },
});
</script>


<template>
  <div :class="classes">
    <!-- Left section -->
    <div class="flex flex-col">
      <div class="flex flex-row items-center">
        <!-- Avatar -->
        <span class="inline-flex items-center justify-center avatar mr-2">
          <img
            v-if="logoUrl"
            :src="logoUrl"
            alt="Logo"
            class="w-5 h-5 rounded-full object-cover"
          >
          <span
            v-else
            class="avatar-fallback"
          >{{ orgInitial }}</span>
        </span>

        <!-- Target name -->
        <div class="font-medium text-sm text-gray-900">
          {{ targetName }}
        </div>

        <!-- Pills -->
        <div class="flex flex-row gap-2 ml-2">
          <div class="pill subscription-pill interval">
            {{ intervalLabel }} Gift
          </div>
          <div class="pill subscription-pill status">
            {{ statusLabel }}
          </div>
        </div>
      </div>

      <!-- Date -->
      <div class="text-xs text-gray-500 mt-0.5">
        {{ ApplicationHelper.formatDate(displayDate, { time: false, weekday: false, year: false }) }}
      </div>
    </div>

    <div class="flex flex-row items-center">
      <div class="text-right md:text-left">
        <div class="text-[10px] tracking-wide text-slate-500">
          Next Giving Date
        </div>
        <div class="text-sm font-semibold text-slate-800">
          {{ nextGivingLabel }}
        </div>
      </div>
      <div
        v-if="clickable"
        class="ml-6"
      >
        <icon
          icon="chevron-right"
          size="10px"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.pill {
  @apply rounded-full px-2 py-0.5 text-[10px] font-medium;
}

.subscription-pill {
  @apply text-[9px] font-semibold px-2 py-0.5 rounded-full leading-tight;

  &.interval {
    color: var(--brand-primary-text, #ffffff);
    background: var(--brand-primary, #10be5a);
    border: var(--brand-primary-border, 1px solid transparent);
  }

  &.status {
    color: var(--brand-accent-text, #ffffff);
    background: var(--brand-accent, #10be5a);
    border: var(--brand-accent-border, 1px solid transparent);
  }
}

.subscription-card {
  @apply flex items-center justify-between px-3 py-3 bg-white rounded-md;
  @apply border border-gray-200 text-base;

  &.subscription-card--alt {
    background: color-mix(in srgb, var(--brand-primary, #10be5a) 5%, #fff);
  }

  &.subscription-card--alt.subscription-card--clickable:hover {
    background: color-mix(in srgb, var(--brand-primary, #10be5a) 7%, #fff);
  }

  &.subscription-card--clickable {
    cursor: pointer;
    &:hover { @apply bg-gray-50; }
    &.active { @apply bg-gray-100; }
  }

  .subscription-amount-pill {
    @apply font-semibold text-xs px-3 py-1 rounded-md whitespace-nowrap;
    color: var(--brand-primary-text, #ffffff);
    background: var(--brand-primary, #10be5a);
    border: var(--brand-primary-border, 1px solid transparent);
  }
}

/* Avatar + fallback */
.avatar {
  @apply w-5 h-5 rounded-full overflow-hidden bg-gray-200 flex-shrink-0;
}
.avatar-fallback {
  @apply w-full h-full flex items-center justify-center text-[10px] font-semibold text-gray-700;
}
</style>
